import { useState } from 'react';

type Tab = 'regular' | 'single';
type Scenario = 'cons' | 'mod' | 'agr';

const BANK = 0.055;
const rates = { cons: 0.07, mod: 0.09, agr: 0.12 };
const scenarioLabels = {
  cons: 'Conservador 7%',
  mod: 'Moderado 9%',
  agr: 'Agresivo 12%'
};

export default function Simulator() {
  const [activeTab, setActiveTab] = useState<Tab>('regular');
  
  // Regular
  const [regularAmount, setRegularAmount] = useState<string>('5000');
  const [regularPeriod, setRegularPeriod] = useState<string>('10');
  const [regularScenario, setRegularScenario] = useState<Scenario>('mod');
  const [regularError, setRegularError] = useState<string>('');
  const [regularData, setRegularData] = useState<any[]>([]);
  
  // Single
  const [singleAmount, setSingleAmount] = useState<string>('50000');
  const [singlePeriod, setSinglePeriod] = useState<string>('10');
  const [singleScenario, setSingleScenario] = useState<Scenario>('mod');
  const [singleError, setSingleError] = useState<string>('');
  const [singleData, setSingleData] = useState<any[]>([]);

  const calculateRegular = () => {
    const aporte = parseFloat(regularAmount);
    const plazo = parseInt(regularPeriod);
    
    if (aporte < 1200) {
      setRegularError('El aporte anual mínimo es de 1.200 USD');
      setRegularData([]);
      return;
    }
    
    setRegularError('');
    const rate = rates[regularScenario];
    let vE = 0;
    let vB = 0;
    const data = [];
    
    for (let i = 1; i <= plazo; i++) {
      vE = vE * (1 + rate) + aporte;
      vB = vB * (1 + BANK) + aporte;
      data.push({ year: i, strategy: vE, bank: vB });
    }
    
    setRegularData(data);
  };

  const calculateSingle = () => {
    const aporte = parseFloat(singleAmount);
    const plazo = parseInt(singlePeriod);
    
    if (aporte < 10000) {
      setSingleError('El aporte único mínimo es de 10.000 USD');
      setSingleData([]);
      return;
    }
    
    setSingleError('');
    const rate = rates[singleScenario];
    const data = [];
    
    for (let i = 1; i <= plazo; i++) {
      data.push({
        year: i,
        strategy: aporte * Math.pow(1 + rate, i),
        bank: aporte * Math.pow(1 + BANK, i)
      });
    }
    
    setSingleData(data);
  };

  const renderChart = (data: any[]) => {
    if (data.length === 0) return null;

    const maxValue = Math.max(...data.map(d => Math.max(d.strategy, d.bank)));
    const width = 100;
    const height = 60;
    const padding = 8;
    
    const scaleX = (index: number) => padding + (index / (data.length - 1)) * (width - 2 * padding);
    const scaleY = (value: number) => height - padding - ((value / maxValue) * (height - 2 * padding));
    
    const strategyPoints = data.map((d, i) => `${scaleX(i)},${scaleY(d.strategy)}`).join(' ');
    const bankPoints = data.map((d, i) => `${scaleX(i)},${scaleY(d.bank)}`).join(' ');
    
    const strategyArea = `${padding},${height - padding} ${strategyPoints} ${width - padding},${height - padding}`;
    const bankArea = `${padding},${height - padding} ${bankPoints} ${width - padding},${height - padding}`;

    return (
      <div className="mt-8 bg-[#0A0A0A] rounded-2xl p-8 border border-[#D4AF37]/20">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-96">
          <defs>
            <linearGradient id="strategyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.05" />
            </linearGradient>
            <linearGradient id="bankGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#6B7280" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#6B7280" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map(y => (
            <line
              key={y}
              x1={padding}
              y1={height - padding - y * (height - 2 * padding) / 100}
              x2={width - padding}
              y2={height - padding - y * (height - 2 * padding) / 100}
              stroke="#374151"
              strokeWidth="0.15"
              strokeDasharray="1,1"
            />
          ))}
          
          {/* Bank area */}
          <polygon
            points={bankArea}
            fill="url(#bankGradient)"
          />
          
          {/* Strategy area */}
          <polygon
            points={strategyArea}
            fill="url(#strategyGradient)"
          />
          
          {/* Bank line */}
          <polyline
            points={bankPoints}
            fill="none"
            stroke="#6B7280"
            strokeWidth="0.4"
            strokeDasharray="1.5,1.5"
          />
          
          {/* Strategy line */}
          <polyline
            points={strategyPoints}
            fill="none"
            stroke="#D4AF37"
            strokeWidth="0.6"
          />
          
          {/* Points on strategy line */}
          {data.map((d, i) => (
            <circle
              key={i}
              cx={scaleX(i)}
              cy={scaleY(d.strategy)}
              r="0.6"
              fill="#D4AF37"
            />
          ))}
          
          {/* Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
            <text
              key={i}
              x={padding - 1.5}
              y={height - padding - ratio * (height - 2 * padding)}
              fill="#9CA3AF"
              fontSize="2.5"
              textAnchor="end"
              dominantBaseline="middle"
            >
              ${(maxValue * ratio / 1000).toFixed(0)}k
            </text>
          ))}
          
          {/* X-axis labels */}
          {data.map((d, i) => {
            if (i % Math.ceil(data.length / 8) === 0 || i === data.length - 1) {
              return (
                <text
                  key={i}
                  x={scaleX(i)}
                  y={height - padding + 3}
                  fill="#9CA3AF"
                  fontSize="2.5"
                  textAnchor="middle"
                >
                  {d.year}
                </text>
              );
            }
            return null;
          })}
        </svg>
        
        <div className="mt-6 flex items-center justify-center gap-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-0.5 bg-[#D4AF37]"></div>
            <span className="text-sm text-gray-400">Estrategia</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-0.5 bg-[#6B7280] border-dashed border-t-2 border-[#6B7280]"></div>
            <span className="text-sm text-gray-400">Banco</span>
          </div>
        </div>
        
        {data.length > 0 && (
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="bg-black/50 rounded-xl p-4 border border-[#D4AF37]/30">
              <p className="text-sm text-gray-400 mb-1">Estrategia</p>
              <p className="text-2xl font-bold text-[#D4AF37]">
                ${data[data.length - 1].strategy.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className="bg-black/50 rounded-xl p-4 border border-gray-600/30">
              <p className="text-sm text-gray-400 mb-1">Banco</p>
              <p className="text-2xl font-bold text-gray-300">
                ${data[data.length - 1].bank.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>
        )}
        
        <p className="text-xs text-gray-500 text-center mt-4">
          * Simulación referencial. Los resultados reales pueden variar.
        </p>
      </div>
    );
  };

  return (
    <section id="simulator" className="py-24 px-6 lg:px-12 bg-[#0F172A]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Simula tu <span className="text-[#D4AF37]">Inversión</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Compara cómo crecería tu dinero con estrategias de índices vs un banco tradicional
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-black/50 rounded-full p-1 border border-[#D4AF37]/20">
            <button
              onClick={() => setActiveTab('regular')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'regular'
                  ? 'bg-[#D4AF37] text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Aportes Regulares (Anuales)
            </button>
            <button
              onClick={() => setActiveTab('single')}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'single'
                  ? 'bg-[#D4AF37] text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Aporte Único
            </button>
          </div>
        </div>

        {/* Regular Tab */}
        {activeTab === 'regular' && (
          <div className="bg-black/30 rounded-2xl p-8 border border-[#D4AF37]/20">
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Aporte Anual (USD)
                </label>
                <input
                  type="number"
                  value={regularAmount}
                  onChange={(e) => setRegularAmount(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="5000"
                />
                {regularError && (
                  <p className="text-red-400 text-sm mt-2">{regularError}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Plazo
                </label>
                <select
                  value={regularPeriod}
                  onChange={(e) => setRegularPeriod(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] pr-8"
                >
                  <option value="5">5 años</option>
                  <option value="10">10 años</option>
                  <option value="15">15 años</option>
                  <option value="20">20 años</option>
                  <option value="25">25 años</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Escenario
                </label>
                <select
                  value={regularScenario}
                  onChange={(e) => setRegularScenario(e.target.value as Scenario)}
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] pr-8"
                >
                  <option value="cons">{scenarioLabels.cons}</option>
                  <option value="mod">{scenarioLabels.mod}</option>
                  <option value="agr">{scenarioLabels.agr}</option>
                </select>
              </div>
            </div>
            
            <button
              onClick={calculateRegular}
              className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-bold py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Calcular Proyección
            </button>
            
            {renderChart(regularData)}
          </div>
        )}

        {/* Single Tab */}
        {activeTab === 'single' && (
          <div className="bg-black/30 rounded-2xl p-8 border border-[#D4AF37]/20">
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Aporte Único (USD)
                </label>
                <input
                  type="number"
                  value={singleAmount}
                  onChange={(e) => setSingleAmount(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37]"
                  placeholder="50000"
                />
                {singleError && (
                  <p className="text-red-400 text-sm mt-2">{singleError}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Plazo
                </label>
                <select
                  value={singlePeriod}
                  onChange={(e) => setSinglePeriod(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] pr-8"
                >
                  <option value="5">5 años</option>
                  <option value="10">10 años</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Escenario
                </label>
                <select
                  value={singleScenario}
                  onChange={(e) => setSingleScenario(e.target.value as Scenario)}
                  className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#D4AF37] pr-8"
                >
                  <option value="cons">{scenarioLabels.cons}</option>
                  <option value="mod">{scenarioLabels.mod}</option>
                  <option value="agr">{scenarioLabels.agr}</option>
                </select>
              </div>
            </div>
            
            <button
              onClick={calculateSingle}
              className="w-full bg-[#D4AF37] hover:bg-[#B8941F] text-black font-bold py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Calcular Proyección
            </button>
            
            {renderChart(singleData)}
          </div>
        )}
      </div>
    </section>
  );
}
